'use client';

import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { Box, VStack } from '@terra/ui-styled-system';
import { Skeleton, Text } from '@terra/ui-design-system';
import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { CoPilotTraceView } from '../chat/CoPilotTraceView';
import { useCoPilotExecutionHistory } from '../../hooks/api/useCoPilotExecutionHistory';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { useOptionalTargetId } from '../../store/projectStore';
import { resolveCoPilotTraceExecutionState } from '../../utils/resolve-co-pilot-trace-status';
import { TraceViewSettingsStoreProvider } from '../../providers/TraceViewSettingsStoreProvider';
import type { CoPilotExecutionMainContentTab } from '../../store/layoutStore';

type CoPilotExecutionTraceTabProps = {
  tab: CoPilotExecutionMainContentTab;
};

export function CoPilotExecutionTraceTab({
  tab,
}: CoPilotExecutionTraceTabProps) {
  const t = useTranslations('chat.copilot.executions');
  const optionalTargetId = useOptionalTargetId();
  const targetId = tab.data.targetId ?? optionalTargetId;
  const threadId = tab.data.threadId;

  const chatStatus = useChatInstancesStore(
    (state) => state.chats.get(threadId)?.status ?? null,
  );

  const { history, isLoading, error, refetch } = useCoPilotExecutionHistory({
    threadId,
    refetchInterval: false,
  });

  const messages = useMemo(() => history as TerraUIMessage[], [history]);

  const { traceStatus, isSessionActive } = useMemo(
    () =>
      resolveCoPilotTraceExecutionState({
        chatStatus,
        history: messages,
      }),
    [chatStatus, messages],
  );

  useEffect(() => {
    if (!isSessionActive) {
      return;
    }

    const intervalId = setInterval(() => {
      void refetch();
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isSessionActive, refetch]);

  if (isLoading) {
    return (
      <VStack p={4} gap={3} alignItems="stretch">
        <Skeleton h="24px" w="40%" borderRadius="sm" />
        <Skeleton h="120px" w="full" borderRadius="md" />
        <Skeleton h="120px" w="full" borderRadius="md" />
      </VStack>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text fontSize="sm" color="fg.error">
          {t('loadError')}
        </Text>
      </Box>
    );
  }

  return (
    <TraceViewSettingsStoreProvider>
      <Box h="full" w="full" overflow="hidden">
        <CoPilotTraceView
          threadId={threadId}
          applicationId={targetId}
          messages={messages}
          status={traceStatus}
          isLoading={false}
        />
      </Box>
    </TraceViewSettingsStoreProvider>
  );
}
